import { Field } from "formik";
import { Form, useForm } from "formik-redux";
import { Fragment, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import isEmpty from "lodash/isEmpty";

import usePrevious from "../../../hooks/use-previous.hook";
import useTranslate from "../../../hooks/use-translate.hook";
import { selectors as funnelSelectors } from "../../../funnel";
import { selectors as phaseSelectors } from "../../../phase";
import { selectors as sourceSelectors } from "../../../source";
import { Dropdown, Switch, UsersFilter, cssUtils } from "../../../ui";
import { editMailtopList } from "./mailtop.actions";
import { MAILTOP_EDIT_LIST_FORM } from "./mailtop.constants";
import { events } from "./mailtop.utils";

const AddList = ({ integrationList, onClose }) => {
    const dispatch = useDispatch();

    const t = useTranslate();

    const funnels = useSelector( funnelSelectors.listAll );
    const phases = useSelector( phaseSelectors.list );
    const sources = useSelector( sourceSelectors.getSources );

    const formik = useForm({
        enableReinitialize: true,
        form: MAILTOP_EDIT_LIST_FORM,
        initialValues: {
            listId: "",
            funnel: { id: "" },
            event: "",
            phase: null,
            createOpportunity: !!integrationList?.sourceId,
            sourceId: null,
            teamId: null,
            usersId: [],
            ...integrationList,
        },
        onSubmit: values => dispatch( editMailtopList( values ) ),
        onSubmitSuccess: onClose,
        validate: values => {
            const errors = {};
            if ( !values.funnel || !values.funnel.id ) {
                errors.funnel = {
                    id: "common:validation.selection"
                };
            }
            if ( !values.event ) {
                errors.event = "common:validation.selection";
            }
            if ( values.createOpportunity ) {
                if ( !values.sourceId ) {
                    errors.sourceId = "common:validation.selection";
                }

                if ( !values.teamId && isEmpty( values.usersId ) ) {
                    errors.usersId = "common:validation.dist-empty-users";
                }
            }
            return errors;
        }
    });

    const handleChange = name => value => formik.setFieldValue( name, value );

    const { setFieldValue } = formik;
    const prevFunnelId = usePrevious( formik.values.funnel.id );

    useEffect( () => {
        if ( prevFunnelId !== formik.values.funnel.id ) {
            setFieldValue( "phase", null );
        }
    }, [ formik.values.funnel.id, prevFunnelId, setFieldValue ] );
    useEffect( () => {
        if ( !formik.values.createOpportunity ) {
            setFieldValue( "sourceId", null );
            setFieldValue( "teamId", null );
            setFieldValue( "usersId", [] );
        }
    }, [ setFieldValue, formik.values.createOpportunity ] );

    const errorUsers = formik.errors && !!formik.errors.usersId && formik.touched?.usersId;
    const integrationListEvent = integrationList ?
        t( `marketplace:automation.mailtop.lists.event.${integrationList.event}.label` ) :
        "";

    return (
        <Dialog open={ !!integrationList } maxWidth="sm" fullWidth>
            <DialogTitle>
                { t( "marketplace:automation.mailtop.lists.edit" ) }
            </DialogTitle>
            <DialogContent>
                <Form formik={ formik }>
                    <Typography color="textSecondary">
                        {
                            t( "marketplace:automation.mailtop.lists.single" ) + ": " +
                            ( integrationList ? integrationList.nome : "" )
                        }
                    </Typography>
                    <Typography color="textSecondary">
                        { `${t( "marketplace:automation.mailtop.lists.event.label" )}: ${integrationListEvent}` }
                    </Typography>
                    <Field
                        name="funnel.id"
                        label={ t( "opportunity:funnel" ) }
                        className={ cssUtils.marginTopSmall }
                        component={ Dropdown }
                        source={ funnels?.map( funnel => ({ value: funnel.id, label: funnel.name }) ) }
                        required
                    />
                    <Field
                        name="phase.id"
                        label={ t( "opportunity:phase" ) }
                        component={ Dropdown }
                        source={ phases?.filter( phase => phase.funnelId === formik.values.funnel.id )
                            .map( phase => ({
                                value: phase.id,
                                label: phase.name
                            }))
                        }
                        sort={ false }
                        disabled={ !formik.values.funnel.id }
                        emptyValue
                        clearParentValue
                    />
                    <Field
                        name="event"
                        label={ t( "marketplace:automation.mailtop.lists.event.label" ) }
                        component={ Dropdown }
                        source={ events.map( event => ({
                            value: event,
                            label: t( `marketplace:automation.mailtop.lists.event.${event}.label` ),
                            helper: t( `marketplace:automation.mailtop.lists.event.${event}.helper` )
                        }))}
                        sort={ false }
                        required
                    />
                    <hr/>
                    <Field
                        name="createOpportunity"
                        type="checkbox"
                        label={ t( "marketplace:automation.mailtop.lists.create-opportunity.label" ) }
                        component={ Switch }
                    />
                    <Typography color="textSecondary" variant="caption">
                        { t( "marketplace:automation.mailtop.lists.create-opportunity.help" ) }
                    </Typography>
                    {
                        formik.values.createOpportunity &&
                            <>
                                <Field
                                    name="sourceId"
                                    label={ t( "opportunity:source" ) }
                                    source={ sources?.map( source => ({
                                        value: source.id,
                                        label: source.name
                                    }))}
                                    component={ Dropdown }
                                    required
                                />
                                <UsersFilter
                                    ComponentRoot={ Fragment }
                                    input={{
                                        team: {
                                            value: formik.values.teamId,
                                            onChange: handleChange( "teamId" )
                                        },
                                        users: {
                                            value: formik.values.usersId,
                                            onChange: handleChange( "usersId" )
                                        }
                                    }}
                                    error={ errorUsers }
                                    fullWidth
                                />
                                {
                                    errorUsers &&
                                        <Typography color="error" variant="caption">
                                            { t( formik.errors.usersId ) }
                                        </Typography>
                                }
                            </>
                    }
                </Form>
            </DialogContent>
            <DialogActions>
                <Button color="primary" onClick={ onClose } disabled={ formik.submitting }>
                    { t( "common:cancel" ) }
                </Button>
                <Button color="primary" onClick={ formik.submitForm } disabled={ formik.submitting }>
                    { t( "common:save" ) }
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddList;